import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../../data/site";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden bg-warm pt-20">
      <div className="container-cando grid items-center gap-12 pb-16 pt-12 lg:grid-cols-12 lg:gap-10 lg:pb-24 lg:pt-16">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="flex items-center gap-4"
          >
            <span className="h-px w-12 bg-bronze" />
            <span className="font-heading text-[13px] font-bold uppercase tracking-[0.26em] text-charcoal">
              Edmonton Residential Construction
            </span>
          </motion.div>

          <h1 data-testid="hero-headline" className="h-display mt-8 text-charcoal">
            <Line delay={0.3}>Built Right.</Line>
            <Line delay={0.44}>
              Built to <span className="text-bronze">Last.</span>
            </Line>
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="lead mt-7 max-w-md text-mute"
          >
            Framing, renovations and additions for Edmonton homeowners.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Link to="/contact" data-testid="hero-cta-estimate" className="btn btn-primary group">
              Get a Project Estimate
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/#projects" data-testid="hero-cta-work" className="btn btn-outline">
              View Our Work
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-9 flex items-center gap-3 text-[14px] text-mute"
            data-testid="hero-trust-line"
          >
            <span className="h-1.5 w-1.5 rotate-45 bg-bronze" />
            Serving Edmonton homeowners with professional residential construction.
          </motion.p>
        </div>

        <div className="lg:col-span-6">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative h-[46vh] overflow-hidden lg:h-[68vh]" data-testid="hero-image-frame">
              <motion.img
                src={IMAGES.hero}
                alt="Premium Edmonton home built by Cando Construction"
                style={{ y: imgY }}
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="h-[115%] w-full object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="absolute -bottom-6 left-0 hidden bg-charcoal px-7 py-5 lg:block"
              data-testid="hero-image-caption"
            >
              <p className="font-heading text-[12px] font-bold uppercase tracking-[0.24em] text-bronze">Edmonton, AB</p>
              <p className="mt-1 text-[14px] text-warm/70">Residential construction, done properly.</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
